import { useEffect, useState, useCallback } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import type { 
  Module, 
  ImpactStream, 
  DbEvidence, 
  EvidenceWithTags,
  EvidenceTag as DbEvidenceTag
} from '../types/database';
import { Evidence, EvidenceTag } from '../types/types';

/**
 * Hook to fetch and manage modules and impact streams
 */
export function useModules() {
  const [modules, setModules] = useState<Module[]>([]);
  const [streams, setStreams] = useState<ImpactStream[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isSupabaseConfigured()) {
      setError('Supabase is not configured');
      setIsLoading(false);
      return;
    }

    async function fetchModulesAndStreams() {
      try {
        // Fetch modules
        const { data: modulesData, error: modulesError } = await supabase
          .from('modules')
          .select('*')
          .order('display_order');

        if (modulesError) throw modulesError;

        // Fetch impact streams
        const { data: streamsData, error: streamsError } = await supabase
          .from('impact_streams')
          .select('*')
          .order('display_order');

        if (streamsError) throw streamsError;

        setModules(modulesData || []);
        setStreams(streamsData || []);
      } catch (err) {
        console.error('Error fetching modules and streams:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
      } finally {
        setIsLoading(false);
      }
    }

    fetchModulesAndStreams();
  }, []);

  return { modules, streams, isLoading, error };
}

/**
 * Hook to fetch evidence with tags from Supabase
 */
export function useEvidenceWithTags() {
  const [evidence, setEvidence] = useState<Evidence[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isSupabaseConfigured()) {
      setError('Supabase is not configured');
      setIsLoading(false);
      return;
    }

    async function fetchEvidence() {
      try {
        // Fetch evidence with tags using the view
        const { data, error: fetchError } = await supabase
          .from('evidence_with_tags')
          .select('*')
          .order('id');

        if (fetchError) throw fetchError;

        // Transform database evidence to app Evidence type
        const transformedEvidence: Evidence[] = (data || []).map((item: EvidenceWithTags) => {
          // Parse the data JSONB field
          const additionalData = item.data || {};
          
          return {
            id: item.id,
            evidence_id: item.evidence_id,
            description: item.description,
            shift_type: item.shift_type || '',
            confidence: item.confidence,
            entities: item.entities || [],
            locations: item.locations || [],
            dates: null,
            temporal_status: item.temporal_status,
            implementation_stage: additionalData.implementation_stage || null,
            evidence_nature: additionalData.evidence_nature || null,
            source_reference: additionalData.source_reference || null,
            data_gaps: additionalData.data_gaps || null,
            requires_clarification: additionalData.requires_clarification || null,
            sharepoint_link: additionalData.sharepoint_link || null,
            accuracy_notes: additionalData.accuracy_notes || null,
            reporting_period: additionalData.reporting_period || null,
            context: additionalData.context || null,
            supporting_details: additionalData.supporting_details || null,
            created_at: item.created_at,
            updated_at: item.updated_at,
            // Add tags array
            tags: item.tags || []
          } as Evidence;
        });

        setEvidence(transformedEvidence);
      } catch (err) {
        console.error('Error fetching evidence:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch evidence');
      } finally {
        setIsLoading(false);
      }
    }

    fetchEvidence();
  }, []);

  return { evidence, isLoading, error };
}

/**
 * Hook to manage tags for evidence
 */
export function useEvidenceTags() {
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Get tags for a specific evidence item
   */
  const getTagsForEvidence = useCallback(async (evidenceId: string): Promise<EvidenceTag[]> => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    // First, get the tags with joined stream and module data
    const { data, error } = await supabase
      .from('evidence_tags')
      .select(`
        evidence_id,
        impact_stream_id,
        impact_streams (
          id,
          name,
          module_id,
          modules (
            id,
            name,
            color
          )
        )
      `)
      .eq('evidence_id', evidenceId);

    if (error) throw error;
    
    // Transform the data into EvidenceTag format
    if (!data) return [];
    
    // Filter out any tags where the impact_stream relationship is missing
    const validData = data.filter(item => item.impact_streams !== null);
    
    if (validData.length !== data.length) {
      console.warn(`Filtered out ${data.length - validData.length} tags with missing impact_streams relationship`);
    }
    
    const transformed = validData.map(item => ({
      stream_id: item.impact_streams.id,
      stream_name: item.impact_streams.name || '',
      module_id: item.impact_streams.modules?.id || item.impact_streams.module_id || '',
      module_name: item.impact_streams.modules?.name || '',
      module_color: item.impact_streams.modules?.color || '#gray'
    }));
    
    return transformed;
  }, []);

  /**
   * Update tags for an evidence item
   */
  const updateTags = useCallback(async (evidenceId: string, streamIds: string[]) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    setIsUpdating(true);
    setError(null);

    try {
      // Start a transaction by deleting existing tags first
      const { error: deleteError } = await supabase
        .from('evidence_tags')
        .delete()
        .eq('evidence_id', evidenceId);

      if (deleteError) throw deleteError;

      // Insert new tags
      if (streamIds.length > 0) {
        const tagsToInsert = streamIds.map(streamId => ({
          evidence_id: evidenceId,
          impact_stream_id: streamId,
          tagged_by: 'user' // You can replace this with actual user ID if you have auth
        }));

        const { error: insertError } = await supabase
          .from('evidence_tags')
          .insert(tagsToInsert);

        if (insertError) throw insertError;
      }

      return { success: true };
    } catch (err) {
      console.error('Error updating tags:', err);
      setError(err instanceof Error ? err.message : 'Failed to update tags');
      return { success: false, error: err };
    } finally {
      setIsUpdating(false);
    }
  }, []);

  /**
   * Add a single tag to evidence
   */
  const addTag = useCallback(async (evidenceId: string, streamId: string) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    const { error } = await supabase
      .from('evidence_tags')
      .insert({
        evidence_id: evidenceId,
        impact_stream_id: streamId,
        tagged_by: 'user'
      });

    if (error && error.code !== '23505') { // Ignore unique constraint violation
      throw error;
    }
  }, []);

  /**
   * Remove a single tag from evidence
   */
  const removeTag = useCallback(async (evidenceId: string, streamId: string) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    const { error } = await supabase
      .from('evidence_tags')
      .delete()
      .eq('evidence_id', evidenceId)
      .eq('impact_stream_id', streamId);

    if (error) throw error;
  }, []);

  return {
    getTagsForEvidence,
    updateTags,
    addTag,
    removeTag,
    isUpdating,
    error
  };
}

/**
 * Hook to fetch evidence statistics grouped by tags
 */
export function useTagStatistics() {
  const [statistics, setStatistics] = useState<{
    byModule: Map<string, number>;
    byStream: Map<string, number>;
    total: number;
    tagged: number;
    untagged: number;
  }>({
    byModule: new Map(),
    byStream: new Map(),
    total: 0,
    tagged: 0,
    untagged: 0
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isSupabaseConfigured()) {
      setIsLoading(false);
      return;
    }

    async function fetchStatistics() {
      try {
        // Get total evidence count
        const { count: totalCount } = await supabase
          .from('evidence')
          .select('*', { count: 'exact', head: true });

        // Get evidence with tags
        const { data: taggedEvidence } = await supabase
          .from('evidence_tags')
          .select(`
            evidence_id,
            impact_streams (
              id,
              name,
              module_id
            )
          `);

        // Calculate statistics
        const byModule = new Map<string, number>();
        const byStream = new Map<string, number>();
        const uniqueTaggedEvidence = new Set<string>();

        (taggedEvidence || []).forEach((tag: any) => {
          uniqueTaggedEvidence.add(tag.evidence_id);
          
          if (tag.impact_streams) {
            // Count by stream
            const streamCount = byStream.get(tag.impact_streams.id) || 0;
            byStream.set(tag.impact_streams.id, streamCount + 1);

            // Count by module
            const moduleCount = byModule.get(tag.impact_streams.module_id) || 0;
            byModule.set(tag.impact_streams.module_id, moduleCount + 1);
          }
        });

        setStatistics({
          byModule,
          byStream,
          total: totalCount || 0,
          tagged: uniqueTaggedEvidence.size,
          untagged: (totalCount || 0) - uniqueTaggedEvidence.size
        });
      } catch (err) {
        console.error('Error fetching statistics:', err);
      } finally {
        setIsLoading(false);
      }
    }

    fetchStatistics();
  }, []);

  return { statistics, isLoading };
}